import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  InputBase,
  Typography,
  Box,
  Popover,
  Paper,
  ListItemButton,
  FormControl,
  Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ErrorMessage } from "formik";
import AvatarIcon from "../../../assets/icons/avatar.svg";
import "./select-owner-component.scss";

export interface OwnerOption {
  value: string;
  label: string;
  email: string;
  avatar?: string | null;
}

export interface AutoSelectProps {
  options: OwnerOption[];
  value: OwnerOption | null;
  onChange: (selectedOption: OwnerOption | null) => void;
  fieldName?: string;
  placeholder?: string;
  isShowError?: boolean;
  handleBlur?: () => void;
  label?: string;
  optional?: boolean;
  isRequired?: boolean;
  inputBasePlaceholder?: string;
}

const SearchableSelectOwner: React.FC<AutoSelectProps> = ({
  options,
  value,
  onChange,
  fieldName,
  placeholder,
  isShowError,
  handleBlur,
  label,
  optional,
  isRequired,
  inputBasePlaceholder = "Search Owner",
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    handleBlur?.();
    setSearchText("");
  };
  useEffect(() => {
    if (anchorEl && inputRef.current) {
      inputRef.current.focus();
    }
  }, [anchorEl]);

  const handleSelect = (option: OwnerOption) => {
    onChange(option);
    handleClose();
  };

  const filteredOptions = useMemo(
    () =>
      options.filter(
        (opt) =>
          opt.label.toLowerCase().includes(searchText.toLowerCase()) ||
          opt.email.toLowerCase().includes(searchText.toLowerCase())
      ),
    [options, searchText]
  );
  return (
    <Box>
      <div className="form-auto-select-component">
        <FormControl fullWidth error={!!isShowError}>
          {label && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "14px",
                marginBottom: "8px",
                fontWeight: 500,
                color: "#000000de", // material default black
              }}
            >
              {label}
              {optional && <span> *</span>}
            </Typography>
          )}

          <Box
            onClick={handleOpen}
            className="select-owner-box"
            tabIndex={0}
            sx={
              isRequired
                ? { borderTop: "2px solid #e30000 !important" }
                : undefined
            }
          >
            <Typography sx={{ color: value && value?.label ? "#000" : "#aaa" }}>
              {value && value?.label ? value.label : placeholder || "Select Owner"}
            </Typography>
            <ExpandMoreIcon />
          </Box>

          <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              style: {
                width: anchorEl?.clientWidth,
              },
            }}
          >
            {/* Search box - stays fixed */}
            <Box
              sx={{
                padding: "8px",
                position: "relative",
              }}
            >
              <SearchIcon
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "16px",
                  transform: "translateY(-50%)",
                  color: "#8D8D8D",
                  pointerEvents: "none",
                  fontSize: 20,
                  zIndex: 1,
                }}
              />
              <InputBase
                inputRef={inputRef}
                placeholder={inputBasePlaceholder}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                fullWidth
                sx={{
                  padding: "8px 12px 8px 40px",
                  border: "1px solid #c2bcbc",
                  borderRadius: "6px",
                  backgroundColor: "#f5f5f5",
                  fontSize: "14px",
                  height: "37px",
                  color: "#000",
                }}
              />
            </Box>

            {/* Scrollable list only */}
            <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <ListItemButton
                    key={option.value}
                    onClick={() => handleSelect(option)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      py: 1.2,
                    }}
                  >
                    <Avatar
                      src={option.avatar || AvatarIcon}
                      alt={option.label}
                      sx={{ width: 40, height: 40 }}
                      onError={(e: any) => {
                        e.target.onerror = null;
                        e.target.src = AvatarIcon;
                      }}
                    />
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {option.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </ListItemButton>
                ))
              ) : (
                <Typography sx={{ padding: "8px 16px", color: "#888" }}>
                  No owners found
                </Typography>
              )}
            </Box>
          </Popover>
        </FormControl>

        {isShowError && (
          <ErrorMessage
            name={fieldName || ""}
            component="div"
            className="error-message"
          />
        )}
      </div>
    </Box>
  );
};

export default SearchableSelectOwner;
